import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { ResponseComment, ResponseThread } from '@/schema'

interface DeletingComment {
  commentId: string
  threadId: string
  documentVersionId: string
}

interface ThreadStoreState {
  commentsByThreadId: Record<string, ResponseComment[]>
  deleteCommentDialogOpen: boolean
  deletingComment: DeletingComment | null
}

interface ThreadStoreActions {
  setThread(threadId: string, comments: ResponseComment[]): void
  addReplyToThread(threadId: string, reply: ResponseComment): void
  updateCommentInThread(threadId: string, comment: ResponseComment): void
  removeCommentFromThread(threadId: string, commentId: string): void
  clearThread(threadId: string): void
  getThreadComments(threadId: string): ResponseThread[]
  getCommentById(
    threadId: string,
    commentId: string
  ): ResponseThread | undefined
  setDeleteCommentDialogOpen(open: boolean): void
  setDeletingComment(comment: DeletingComment | null): void
  clearDeletingComment(): void
  reset(): void
}

type ThreadStore = ThreadStoreState & ThreadStoreActions

const threadStoreInitialState: ThreadStoreState = {
  commentsByThreadId: {},
  deleteCommentDialogOpen: false,
  deletingComment: null,
}

export const useThreadStore = create<ThreadStore>()(
  devtools(
    (set, get) => ({
      ...threadStoreInitialState,

      setThread: (threadId: string, comments: ResponseComment[]) => {
        set(
          (state) => ({
            commentsByThreadId: {
              ...state.commentsByThreadId,
              [threadId]: comments,
            },
          }),
          false,
          'setThread'
        )
      },

      addReplyToThread: (threadId: string, reply: ResponseComment) => {
        set(
          (state) => {
            const existingComments = state.commentsByThreadId[threadId] || []
            return {
              commentsByThreadId: {
                ...state.commentsByThreadId,
                [threadId]: [...existingComments, reply],
              },
            }
          },
          false,
          'addReplyToThread'
        )
      },

      updateCommentInThread: (threadId: string, comment: ResponseComment) => {
        set(
          (state) => {
            const existingComments = state.commentsByThreadId[threadId] || []
            return {
              commentsByThreadId: {
                ...state.commentsByThreadId,
                [threadId]: existingComments.map((c) =>
                  c.id === comment.id
                    ? {
                        ...c,
                        content: comment.content,
                        content_last_edited_at: comment.content_last_edited_at,
                      }
                    : c
                ),
              },
            }
          },
          false,
          'updateCommentInThread'
        )
      },

      removeCommentFromThread: (threadId: string, commentId: string) => {
        set(
          (state) => {
            const existingComments = state.commentsByThreadId[threadId] || []
            return {
              commentsByThreadId: {
                ...state.commentsByThreadId,
                [threadId]: existingComments.filter((c) => c.id !== commentId),
              },
            }
          },
          false,
          'removeCommentFromThread'
        )
      },

      clearThread: (threadId: string) => {
        set(
          (state) => {
            const { [threadId]: _, ...rest } = state.commentsByThreadId
            return {
              commentsByThreadId: rest,
            }
          },
          false,
          'clearThread'
        )
      },

      getThreadComments: (threadId: string) => {
        return get().commentsByThreadId[threadId] || []
      },

      getCommentById: (threadId: string, commentId: string) => {
        const comments = get().commentsByThreadId[threadId] || []
        return comments.find((c) => c.id === commentId)
      },

      setDeleteCommentDialogOpen: (open: boolean) => {
        set(
          { deleteCommentDialogOpen: open },
          false,
          'setDeleteCommentDialogOpen'
        )
      },

      setDeletingComment: (comment: DeletingComment | null) => {
        set({ deletingComment: comment }, false, 'setDeletingComment')
      },

      clearDeletingComment: () => {
        set({ deletingComment: null }, false, 'clearDeletingComment')
      },

      reset: () => {
        set(threadStoreInitialState, false, 'reset')
      },
    }),
    {
      name: 'ThreadStore',
    }
  )
)
